/* Regnum "Add to Home Screen" banner.
   Branded install prompt for the PWA (manifest shipped in theme assets).

   Behaviour contract:
   - Never shows inside the installed app (standalone display mode).
   - Never shows after install (appinstalled flag) or within 30 days of dismiss.
   - Shows on the 2nd pageview of a session, after a 3s delay, mobile only.
     Mobile shows the banner unconditionally: if Chromium has volunteered
     beforeinstallprompt by tap time the Install button fires the native
     dialog, otherwise the banner falls back to "open your browser menu"
     instructions (Chrome withholds the event when the app is already
     installed or a previous native prompt was recently dismissed).
   - Desktop only ever shows the banner when beforeinstallprompt fires.
   - iOS: no install API exists, so the banner shows Share > Add to Home
     Screen instructions instead (works in Safari, Chrome, Edge, Firefox
     since iOS 16.4). In-app browsers (Instagram, WhatsApp etc.) cannot
     install at all and never see the banner. */
(function () {
  'use strict';

  var script = document.currentScript;
  var ICON = script && script.dataset && script.dataset.icon ? script.dataset.icon : '';
  var SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;

  /* QA override: append ?pwa-banner=test to any URL to show the banner
     immediately, bypassing the engagement gates (mobile-only, 2nd pageview,
     delay, snooze). Standalone mode is still respected. Logs gate state. */
  var FORCE = /[?&]pwa-banner=test(&|$)/.test(window.location.search);

  /* Always log the gate decision as a single console line so "why is the
     banner not showing" is answerable from any device's console. */
  function info() {
    try {
      console.info.apply(console, ['[regnum-pwa]'].concat(Array.prototype.slice.call(arguments)));
    } catch (e) {}
  }
  var debug = info;

  if (window.matchMedia('(display-mode: standalone)').matches) { info('not showing: running as the installed app'); return; }
  if (window.navigator.standalone === true) { info('not showing: running as the installed app (iOS)'); return; }

  function lsGet(key) { try { return localStorage.getItem(key); } catch (e) { return null; } }
  function lsSet(key, value) { try { localStorage.setItem(key, value); } catch (e) {} }

  if (!FORCE) {
    if (lsGet('regnum-pwa-installed') === '1') { info('not showing: app was installed from this browser before. Reset: localStorage.removeItem("regnum-pwa-installed")'); return; }
    var snoozedAt = parseInt(lsGet('regnum-pwa-snoozed') || '0', 10);
    if (snoozedAt && Date.now() - snoozedAt < SNOOZE_MS) { info('not showing: banner was dismissed recently. Reset: localStorage.removeItem("regnum-pwa-snoozed")'); return; }
  }

  var views = 2;
  try {
    views = (parseInt(sessionStorage.getItem('regnum-pwa-views') || '0', 10) || 0) + 1;
    sessionStorage.setItem('regnum-pwa-views', String(views));
  } catch (e) {}

  var ua = navigator.userAgent;
  var isIos = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  var isInApp = /Instagram|FBAN|FBAV|FB_IAB|WhatsApp|Snapchat|TikTok|Line\//i.test(ua);
  /* Any real iOS browser gets the Share instructions: Safari natively, and
     since iOS 16.4 Chrome/Edge/Firefox also expose Add to Home Screen in
     their share menus. In-app webviews cannot install and are excluded. */
  var isIosBrowser = isIos && !isInApp;

  var deferredPrompt = null;
  var banner = null;
  var scheduled = false;

  function isMobile() {
    return window.matchMedia('(max-width: 999px)').matches;
  }

  function maybeShow() {
    if (scheduled) return;
    if (FORCE) {
      debug('force mode: showing immediately', { mobileViewport: isMobile(), views: views, iosBrowser: isIosBrowser, installPromptCaptured: !!deferredPrompt });
      scheduled = true;
      show();
      return;
    }
    if (!isMobile()) { info('not showing: viewport wider than 999px. Desktop only shows when the browser offers install.'); return; }
    if (views < 2) { info('not showing yet: pageview 1 of 2 this session (note: pages still cached without this script do not count)'); return; }
    scheduled = true;
    info('showing in 3 seconds');
    setTimeout(show, 3000);
  }

  function show() {
    if (banner) return;
    banner = build();
    document.body.appendChild(banner);
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        banner.classList.add('visible');
      });
    });
    setTimeout(function () {
      if (!banner) return;
      var cs = window.getComputedStyle(banner);
      info('banner state: connected=' + banner.isConnected + ' display=' + cs.display + ' opacity=' + cs.opacity + ' position=' + cs.position + ' (position must be "fixed"; if "static", this page is still serving the old stylesheet)');
    }, 600);
  }

  function hide() {
    if (!banner) return;
    var el = banner;
    banner = null;
    el.classList.remove('visible');
    setTimeout(function () { el.remove(); }, 350);
  }

  function snooze() {
    if (!FORCE) lsSet('regnum-pwa-snoozed', String(Date.now()));
    hide();
  }

  var SHARE_SVG =
    '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="vertical-align:-2px">' +
    '<path d="M12 3v12"/><path d="M8 7l4-4 4 4"/><path d="M5 11v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8"/></svg>';

  function build() {
    var el = document.createElement('div');
    el.className = 'regnum-a2hs';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-label', 'Install the Regnum app');

    var body =
      '<button type="button" class="regnum-a2hs-close" aria-label="Dismiss">' +
      '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
      '</button>' +
      '<div class="regnum-a2hs-row">' +
      (ICON ? '<img class="regnum-a2hs-icon" src="' + ICON + '" width="48" height="48" alt="" loading="lazy">' : '') +
      '<div class="regnum-a2hs-copy">' +
      '<p class="regnum-a2hs-title">Regnum, one tap away</p>';

    if (isIosBrowser) {
      body +=
        '<p class="regnum-a2hs-text">Tap ' + SHARE_SVG + ' <strong>Share</strong>, then <strong>Add to Home Screen</strong> for quick access to groceries, home and gifts.</p>' +
        '</div></div>';
    } else {
      body +=
        '<p class="regnum-a2hs-text">Quick access to groceries, home and gifts.</p>' +
        '</div></div>' +
        '<button type="button" class="regnum-a2hs-install">Add to home screen</button>';
    }
    el.innerHTML = body;

    el.querySelector('.regnum-a2hs-close').addEventListener('click', snooze);

    var installBtn = el.querySelector('.regnum-a2hs-install');
    if (installBtn) {
      installBtn.addEventListener('click', function () {
        if (!deferredPrompt) {
          debug('install clicked but no beforeinstallprompt was captured (already installed, browser cooldown, or unsupported browser)');
          var copy = banner && banner.querySelector('.regnum-a2hs-text');
          if (copy) copy.innerHTML = 'Open your browser menu and choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.';
          if (!FORCE) lsSet('regnum-pwa-snoozed', String(Date.now()));
          return;
        }
        var promptEvent = deferredPrompt;
        deferredPrompt = null;
        promptEvent.prompt();
        promptEvent.userChoice.then(function (choice) {
          if (choice && choice.outcome === 'accepted') {
            lsSet('regnum-pwa-installed', '1');
            hide();
          } else {
            snooze();
          }
        });
      });
    }
    return el;
  }

  window.addEventListener('beforeinstallprompt', function (e) {
    e.preventDefault();
    deferredPrompt = e;
    debug('beforeinstallprompt captured');
    maybeShow();
  });

  window.addEventListener('appinstalled', function () {
    lsSet('regnum-pwa-installed', '1');
    hide();
  });

  if (isIosBrowser || FORCE || isMobile()) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', maybeShow);
    } else {
      maybeShow();
    }
  } else {
    info('not showing: desktop viewport. The banner appears here only if the browser offers install (waiting for beforeinstallprompt).');
  }

  if (FORCE) {
    setTimeout(function () {
      if (!deferredPrompt && !isIosBrowser) {
        debug('no beforeinstallprompt after 5s. Likely causes: app already installed on this device, a recent native-prompt dismissal cooldown, or check DevTools > Application > Manifest for installability errors.');
      }
    }, 5000);
  }
})();
